import { z } from 'zod';
import { canonicalJsonStringify, compareInstants, countryCodeSchema, instantSchema, opaqueSubjectSchema, semverSchema, sha256Schema, sha256CanonicalJson, uuidSchema, } from './primitives.js';
import { revisionMetadataSchema } from './versioning.js';
export const userPreferencesSchema = z.strictObject({
    user_preferences_id: uuidSchema,
    locale: z.enum(['cs-CZ', 'de-DE']),
    theme: z.enum(['light', 'dark', 'system']),
    distance_unit: z.literal('km'),
    home_country: countryCodeSchema.nullable(),
    default_role: z.enum(['driver', 'second_driver', 'instructor', 'transport', 'other']).nullable(),
    default_work_type: z
        .enum(['passenger', 'freight', 'shunting', 'light_engine', 'service_run', 'other'])
        .nullable(),
    route_suggestions_enabled: z.boolean(),
    reduce_motion_override: z.enum(['system', 'reduce', 'allow']),
    large_touch_targets: z.literal(true),
    metadata: revisionMetadataSchema,
});
export const accountIdentitySchema = z.strictObject({
    issuer: z
        .string()
        .url()
        .refine((value) => {
        const url = new URL(value);
        return (url.protocol === 'https:' &&
            url.origin === value &&
            url.username === '' &&
            url.password === '');
    }, 'issuer must be an HTTPS origin without path, credentials, query, or fragment'),
    subject: opaqueSubjectSchema,
});
export const ACCOUNT_IDENTITY_DIGEST_VERSION = 'account_identity_sha256.v1';
export function accountIdentityCanonicalJson(input) {
    return canonicalJsonStringify(accountIdentitySchema.parse(input));
}
export function accountIdentitySha256(input) {
    const identity = accountIdentitySchema.parse(input);
    return sha256CanonicalJson('dmf.account-identity.sha256.v1', identity);
}
export const verifiedExportEvidenceSchema = z.strictObject({
    export_id: uuidSchema,
    local_profile_id: uuidSchema,
    dataset_epoch: uuidSchema,
    export_sha256: sha256Schema,
    schema_version: semverSchema,
    verified_at: instantSchema,
});
export const localDatabaseBindingSchema = z
    .strictObject({
    local_profile_id: uuidSchema,
    database_name: z
        .string()
        .regex(/^dmf-profile-[0-9a-f-]{36}$/, 'database name must be profile-scoped'),
    account_identity_sha256: sha256Schema.nullable(),
    account_identity_digest_version: z.literal(ACCOUNT_IDENTITY_DIGEST_VERSION).nullable(),
})
    .superRefine((value, context) => {
    if (value.database_name !== `dmf-profile-${value.local_profile_id}`) {
        context.addIssue({
            code: 'custom',
            path: ['database_name'],
            message: 'database_name must be derived from local_profile_id',
        });
    }
    if ((value.account_identity_sha256 === null) !==
        (value.account_identity_digest_version === null)) {
        context.addIssue({
            code: 'custom',
            path: ['account_identity_digest_version'],
            message: 'account identity digest and derivation version must be present together',
        });
    }
});
export const localProfileSchema = z
    .strictObject({
    contract_version: z.literal('local_profile.v1'),
    local_profile_id: uuidSchema,
    database_binding: localDatabaseBindingSchema,
    created_at: instantSchema,
    account_identity: accountIdentitySchema.nullable(),
    profile_state: z.enum(['active', 'locked', 'wipe_required']),
    anonymous_transfer_state: z.enum(['none', 'explicit_transfer_pending']),
    dataset_epoch: uuidSchema,
    indexeddb_schema_version: z.number().int().safe().positive(),
    last_mutation_at: instantSchema.nullable(),
    last_successful_sync_at: instantSchema.nullable(),
    last_verified_export: verifiedExportEvidenceSchema.nullable(),
})
    .superRefine((value, context) => {
    if (value.database_binding.local_profile_id !== value.local_profile_id) {
        context.addIssue({
            code: 'custom',
            path: ['database_binding', 'local_profile_id'],
            message: 'database binding must belong to local profile',
        });
    }
    if ((value.account_identity === null) !==
        (value.database_binding.account_identity_sha256 === null)) {
        context.addIssue({
            code: 'custom',
            path: ['database_binding', 'account_identity_sha256'],
            message: 'account binding digest must be present exactly for a bound profile',
        });
    }
    if (value.account_identity !== null &&
        value.database_binding.account_identity_sha256 !==
            accountIdentitySha256(value.account_identity)) {
        context.addIssue({
            code: 'custom',
            path: ['database_binding', 'account_identity_sha256'],
            message: 'account binding digest must derive from the canonical bound account identity',
        });
    }
    if (value.account_identity === null && value.profile_state === 'locked') {
        context.addIssue({
            code: 'custom',
            path: ['profile_state'],
            message: 'an anonymous profile cannot be account-locked',
        });
    }
    if (value.account_identity !== null && value.anonymous_transfer_state !== 'none') {
        context.addIssue({
            code: 'custom',
            path: ['anonymous_transfer_state'],
            message: 'a bound profile cannot have a pending anonymous transfer',
        });
    }
    if (value.last_verified_export) {
        if (value.last_verified_export.local_profile_id !== value.local_profile_id ||
            value.last_verified_export.dataset_epoch !== value.dataset_epoch) {
            context.addIssue({
                code: 'custom',
                path: ['last_verified_export'],
                message: 'verified export must belong to this profile and dataset epoch',
            });
        }
    }
    for (const [field, timestamp] of [
        ['last_mutation_at', value.last_mutation_at],
        ['last_successful_sync_at', value.last_successful_sync_at],
        ['last_verified_export', value.last_verified_export?.verified_at ?? null],
    ]) {
        if (timestamp !== null && compareInstants(timestamp, value.created_at) < 0) {
            context.addIssue({
                code: 'custom',
                path: [field],
                message: `${field} must not precede profile creation`,
            });
        }
    }
});
export const anonymousProfileTransferSchema = z
    .strictObject({
    transfer_id: uuidSchema,
    local_profile_id: uuidSchema,
    target_account_identity: accountIdentitySchema,
    source_dataset_epoch: uuidSchema,
    explicit_user_confirmation_at: instantSchema,
    resulting_account_identity_sha256: sha256Schema,
    account_identity_digest_version: z.literal(ACCOUNT_IDENTITY_DIGEST_VERSION),
})
    .superRefine((value, context) => {
    if (value.resulting_account_identity_sha256 !==
        accountIdentitySha256(value.target_account_identity)) {
        context.addIssue({
            code: 'custom',
            path: ['resulting_account_identity_sha256'],
            message: 'transfer digest must derive from the canonical target account identity',
        });
    }
});
export const accountSwitchPreconditionsSchema = z.strictObject({
    source_local_profile_id: uuidSchema,
    source_dataset_epoch: uuidSchema,
    source_account_identity: accountIdentitySchema,
    target_account_identity: accountIdentitySchema,
    last_mutation_at: instantSchema.nullable(),
    outbox_empty: z.boolean(),
    last_successful_sync_at: instantSchema.nullable(),
    verified_export: verifiedExportEvidenceSchema,
    explicit_wipe_confirmation: z.boolean(),
});
export const profileWipeReceiptSchema = z
    .strictObject({
    wipe_receipt_id: uuidSchema,
    local_profile_id: uuidSchema,
    database_name: z.string().min(1).max(256),
    cache_storage_cleared: z.literal(true),
    indexeddb_deleted: z.literal(true),
    completed_at: instantSchema,
})
    .superRefine((value, context) => {
    if (value.database_name !== `dmf-profile-${value.local_profile_id}`) {
        context.addIssue({
            code: 'custom',
            path: ['database_name'],
            message: 'wipe receipt must identify the exact profile-scoped database',
        });
    }
});
export function evaluateLocalProfileAccess(profileInput, requestedAccountInput) {
    const profile = localProfileSchema.parse(profileInput);
    const requestedAccount = requestedAccountInput === null ? null : accountIdentitySchema.parse(requestedAccountInput);
    if (profile.profile_state === 'wipe_required') {
        return { allowed: false, required_action: 'complete_wipe' };
    }
    if (profile.account_identity === null) {
        return requestedAccount === null
            ? { allowed: true, required_action: 'none' }
            : { allowed: false, required_action: 'explicit_anonymous_transfer' };
    }
    if (requestedAccount === null) {
        return profile.profile_state === 'active'
            ? { allowed: true, required_action: 'none' }
            : { allowed: false, required_action: 'authenticate_same_account' };
    }
    if (sameAccount(profile.account_identity, requestedAccount)) {
        return profile.profile_state === 'active'
            ? { allowed: true, required_action: 'none' }
            : { allowed: false, required_action: 'authenticate_same_account' };
    }
    return { allowed: false, required_action: 'complete_sync_export_and_wipe' };
}
export function accountSwitchIsReady(input) {
    const parsed = accountSwitchPreconditionsSchema.parse(input);
    const evidenceAfterMutation = parsed.last_mutation_at === null ||
        (parsed.last_successful_sync_at !== null &&
            compareInstants(parsed.last_successful_sync_at, parsed.last_mutation_at) >= 0 &&
            compareInstants(parsed.verified_export.verified_at, parsed.last_mutation_at) >= 0);
    return (!sameAccount(parsed.source_account_identity, parsed.target_account_identity) &&
        parsed.verified_export.local_profile_id === parsed.source_local_profile_id &&
        parsed.verified_export.dataset_epoch === parsed.source_dataset_epoch &&
        parsed.outbox_empty &&
        evidenceAfterMutation &&
        parsed.explicit_wipe_confirmation);
}
function sameAccount(left, right) {
    return left.issuer === right.issuer && left.subject === right.subject;
}
//# sourceMappingURL=profile.js.map